import { useState } from 'react';
import { AnimatePresence, motion } from 'framer-motion';
import { ChevronDown, HelpCircle } from 'lucide-react';

const DIR = {
  strongly_bullish: { icon: '↑↑', label: 'Strongly Bullish', color: '#10B981', bg: 'rgba(16,185,129,0.15)' },
  bullish:          { icon: '↑',  label: 'Bullish',          color: '#34D399', bg: 'rgba(52,211,153,0.12)' },
  neutral:          { icon: '→',  label: 'Neutral',          color: '#D4D4D8', bg: 'rgba(212,212,216,0.10)' },
  bearish:          { icon: '↓',  label: 'Bearish',          color: '#F87171', bg: 'rgba(248,113,113,0.12)' },
  strongly_bearish: { icon: '↓↓', label: 'Strongly Bearish', color: '#EF4444', bg: 'rgba(239,68,68,0.15)' },
};

// Map a 0..100 score → direction key.
function directionFromScore(score) {
  if (score == null) return 'neutral';
  if (score >= 80) return 'strongly_bullish';
  if (score >= 65) return 'bullish';
  if (score <= 20) return 'strongly_bearish';
  if (score <= 35) return 'bearish';
  return 'neutral';
}

const fmtPrice = (p) => {
  if (p == null) return null;
  if (p >= 1000) return `$${p.toLocaleString(undefined, { maximumFractionDigits: 0 })}`;
  if (p >= 10)   return `$${p.toFixed(0)}`;
  return `$${p.toFixed(2)}`;
};


/**
 * Dual-lens frame for Investor cards.
 *
 * Left box  = "CURRENT TREND"     — derived from the entity's hotness_score
 *                                    + score_breakdown (our internal pulse).
 * Right box = "ANALYST PROJECTION" — derived from entity.analyst_consensus
 *                                    (FMP + yfinance aggregated 12-month target).
 *
 * Each box is independently expandable. Both boxes render high-contrast white
 * typography over the card's dark background, with a 3-px direction-coloured
 * accent strip on the left edge.
 *
 * Per WTFH_Projection_Engine spec + May 2026 user-feedback: "make it bigger,
 * white, bold and visible".
 */
export default function SignalLensFrame({ entity, persona = 'investor' }) {
  const [openLeft, setOpenLeft] = useState(false);
  const [openRight, setOpenRight] = useState(false);
  const [analystDetail, setAnalystDetail] = useState(null);
  const [loadingAnalyst, setLoadingAnalyst] = useState(false);

  const isReseller = persona === 'reseller';
  // Left lens label — "Trend Score" reads more naturally for reseller cards
  // than "Current Trend" (May 2026 user request).
  const leftLabel = isReseller ? 'Trend Score' : 'Current Trend';
  // Right lens label — Reseller has no analysts so we call the box "Projection"
  // (renamed from "Analyst Projection") and sourced from the same projection
  // engine output stored in entity.analyst_consensus.
  const rightLabel = isReseller ? 'Projection' : 'Analyst Projection';

  // ── Left lens — Current Trend (internal pulse) ──
  const score = Math.round(entity.hotness_score || 0);
  const trendDir = DIR[directionFromScore(score)];
  const breakdown = entity.score_breakdown || {};

  // Confidence proxy for current trend: source diversity saturated at 10 sources.
  const trendConf = Math.min(100, ((entity.source_count || 0) / 10) * 100);
  const trendConfLabel = trendConf >= 75 ? 'High' : trendConf >= 50 ? 'Med' : 'Low';

  // ── Right lens — Analyst Projection ──
  const ac = entity.analyst_consensus || null;
  const acDir = ac?.direction ? DIR[ac.direction] : null;
  const acTarget = ac?.target_price ? fmtPrice(ac.target_price) : null;
  const acConfPct = ac?.confidence != null ? Math.round(ac.confidence * 100) : null;
  const acConfLabel = acConfPct == null ? null : acConfPct >= 75 ? 'High' : acConfPct >= 50 ? 'Med' : 'Low';
  const hasAnalyst = !!ac && !!ac.direction;

  const fetchAnalystDetail = async () => {
    if (analystDetail || loadingAnalyst || !hasAnalyst) return;
    if (entity._projection_detail) {
      setAnalystDetail(entity._projection_detail);
      return;
    }
    setLoadingAnalyst(true);
    try {
      const API = process.env.REACT_APP_BACKEND_URL;
      const res = await fetch(`${API}/api/analyst-predictions/${entity.canonical_id}?persona=${persona}`);
      if (res.ok) setAnalystDetail(await res.json());
    } finally {
      setLoadingAnalyst(false);
    }
  };

  return (
    <div className="px-3 pb-2" data-testid={`signal-lens-${entity.canonical_id}`}>
        <div className="grid grid-cols-2 rounded-lg overflow-hidden"
        style={{
          background: 'rgba(255,255,255,0.02)',
          border: '1px solid rgba(255,255,255,0.10)',
        }}
      >
        {/* ─────────── LEFT BOX — CURRENT TREND ─────────── */}
        <div
          className="relative"
          style={{ borderRight: '1px solid rgba(255,255,255,0.08)' }}
          data-testid={`current-trend-${entity.canonical_id}`}
        >
          <div
            className="absolute top-0 left-0 bottom-0"
            style={{ width: 3, background: trendDir.color }}
          />
          <button
            type="button"
            onClick={(e) => { e.stopPropagation(); setOpenLeft(v => !v); }}
            data-testid={`current-trend-toggle-${entity.canonical_id}`}
            className="w-full text-left px-3 py-2.5 hover:bg-white/[0.03] transition-colors"
          >
            <div className="flex items-center gap-2 mb-1.5">
              <span
                className="text-[11px] font-black uppercase tracking-[0.18em] text-white"
                style={{ fontFamily: 'Manrope, sans-serif' }}
              >
                {leftLabel}
              </span>
            </div>
            <div className="flex items-center justify-between gap-3">
              <div className="flex items-center gap-2">
                <span className="text-2xl font-black leading-none" style={{ color: trendDir.color }}>
                  {trendDir.icon}
                </span>
                <span
                  className="text-base font-black uppercase tracking-tight text-white"
                  style={{ fontFamily: 'Manrope, sans-serif' }}
                >
                  {trendDir.label}
                </span>
              </div>
              <span className="flex items-baseline gap-0.5">
                <span
                  className="text-2xl font-black tabular-nums"
                  style={{ color: trendDir.color, fontFamily: 'JetBrains Mono, monospace' }}
                >
                  {score}
                </span>
                <span className="text-sm font-bold text-white/70" style={{ fontFamily: 'JetBrains Mono, monospace' }}>
                  /100
                </span>
              </span>
            </div>
            {/* Confidence bar */}
            <div className="flex items-center gap-2 mt-1.5">
              <div className="flex-1 h-1.5 rounded-full overflow-hidden" style={{ background: 'rgba(255,255,255,0.08)' }}>
                <div
                  className="h-full rounded-full"
                  style={{
                    width: `${trendConf}%`,
                    background: trendConf >= 50 ? trendDir.color : '#9CA3AF',
                    opacity: trendConf >= 75 ? 1 : 0.8,
                  }}
                />
              </div>
              <span
                className="text-[11px] font-bold uppercase tracking-wide text-white/80 tabular-nums"
                style={{ fontFamily: 'JetBrains Mono, monospace' }}
              >
                {Math.round(trendConf)}% {trendConfLabel}
              </span>
            </div>
            {/* See-why affordance — clearer than a bare chevron (May 2026) */}
            <div className="flex items-center gap-1.5 mt-1.5 pt-1.5"
                 style={{ borderTop: '1px dashed rgba(255,255,255,0.10)' }}>
              <HelpCircle size={12} style={{ color: trendDir.color }} strokeWidth={2.5} />
              <span
                className="text-[11px] font-black uppercase tracking-wider"
                style={{ color: '#fff', fontFamily: 'Manrope, sans-serif' }}
              >
                {openLeft ? 'Hide' : 'See why'}
              </span>
              <ChevronDown
                size={14}
                style={{
                  color: '#fff',
                  transform: openLeft ? 'rotate(180deg)' : 'rotate(0)',
                  transition: 'transform 180ms',
                  marginLeft: 'auto',
                }}
                strokeWidth={2.75}
              />
            </div>
          </button>

          <AnimatePresence>
            {openLeft && (
              <motion.div
                initial={{ height: 0, opacity: 0 }}
                animate={{ height: 'auto', opacity: 1 }}
                exit={{ height: 0, opacity: 0 }}
                transition={{ duration: 0.18 }}
                className="overflow-hidden"
                data-testid={`current-trend-detail-${entity.canonical_id}`}
              >
                <div
                  className="px-4 pb-3"
                  style={{ borderTop: '1px solid rgba(255,255,255,0.08)', paddingTop: 12 }}
                >
                  <div
                    className="text-[11px] font-bold uppercase tracking-wider text-white/70 mb-2"
                    style={{ fontFamily: 'Manrope, sans-serif' }}
                  >
                    How we got to {score}/100
                  </div>
                  <BreakdownBars breakdown={breakdown} dirColor={trendDir.color} buckets={ac?.buckets} isReseller={isReseller} />
                  {entity.why_trending && (
                    <p
                      className="text-[12px] leading-relaxed text-white/90 mt-3"
                      style={{ fontFamily: 'Manrope, sans-serif' }}
                    >
                      {entity.why_trending}
                    </p>
                  )}
                </div>
              </motion.div>
            )}
          </AnimatePresence>
        </div>

        {/* ─────────── RIGHT BOX — ANALYST PROJECTION ─────────── */}
        <div className="relative" data-testid={`analyst-projection-${entity.canonical_id}`}>
          <div
            className="absolute top-0 left-0 bottom-0"
            style={{ width: 3, background: acDir?.color || '#6B7280' }}
          />
          <button
            type="button"
            onClick={(e) => {
              e.stopPropagation();
              setOpenRight(v => !v);
              if (!openRight) fetchAnalystDetail();
            }}
            data-testid={`analyst-projection-toggle-${entity.canonical_id}`}
            disabled={!hasAnalyst}
            className="w-full text-left px-3 py-2.5 hover:bg-white/[0.03] transition-colors disabled:opacity-60"
          >
            <div className="flex items-center gap-2 mb-2">
              <span
                className="text-[11px] font-black uppercase tracking-[0.18em] text-white"
                style={{ fontFamily: 'Manrope, sans-serif' }}
              >
                {rightLabel}
              </span>
            </div>
            {hasAnalyst ? (
              <>
                <div className="flex items-center justify-between gap-3">
                  <div className="flex items-center gap-2">
                    <span className="text-2xl font-black leading-none" style={{ color: acDir.color }}>
                      {acDir.icon}
                    </span>
                    <span
                      className="text-base font-black uppercase tracking-tight text-white"
                      style={{ fontFamily: 'Manrope, sans-serif' }}
                    >
                      {acDir.label}
                    </span>
                  </div>
                  <span className="flex items-baseline gap-2">
                    {acTarget && (
                      <span
                        className="text-2xl font-black tabular-nums"
                        style={{ color: acDir.color, fontFamily: 'JetBrains Mono, monospace' }}
                      >
                        {acTarget}
                      </span>
                    )}
                  </span>
                </div>
                {/* Confidence + horizon row */}
                <div className="flex items-center gap-2 mt-1.5">
                  <div className="flex-1 h-1.5 rounded-full overflow-hidden" style={{ background: 'rgba(255,255,255,0.08)' }}>
                    <div
                      className="h-full rounded-full"
                      style={{
                        width: `${acConfPct || 0}%`,
                        background: (acConfPct || 0) >= 50 ? acDir.color : '#9CA3AF',
                        opacity: (acConfPct || 0) >= 75 ? 1 : 0.8,
                      }}
                    />
                  </div>
                  <span
                    className="text-[11px] font-bold uppercase tracking-wide text-white/80 tabular-nums"
                    style={{ fontFamily: 'JetBrains Mono, monospace' }}
                  >
                    {acConfPct || 0}% {acConfLabel || ''}
                  </span>
                </div>
                {/* Sub-meta — upside, horizon, analyst count (white & visible) */}
                <div className="flex items-center gap-2.5 mt-1.5 flex-wrap">
                  {ac.upside_pct != null && (
                    <span
                      className="text-[12px] font-black tabular-nums"
                      style={{
                        color: ac.upside_pct >= 0 ? '#10B981' : '#F87171',
                        fontFamily: 'JetBrains Mono, monospace',
                      }}
                    >
                      {ac.upside_pct > 0 ? '+' : ''}{ac.upside_pct}% upside
                    </span>
                  )}
                  <span className="text-[11px] font-bold text-white/80 uppercase tracking-wide">
                    {ac.time_horizon || '12M'}
                  </span>
                  {ac.analysts_count != null && (
                    <span className="text-[11px] font-bold text-white/80">
                      · {ac.analysts_count} analysts
                    </span>
                  )}
                </div>
                {/* See-why affordance */}
                <div className="flex items-center gap-1.5 mt-1.5 pt-1.5"
                     style={{ borderTop: '1px dashed rgba(255,255,255,0.10)' }}>
                  <HelpCircle size={12} style={{ color: acDir.color }} strokeWidth={2.5} />
                  <span
                    className="text-[11px] font-black uppercase tracking-wider"
                    style={{ color: '#fff', fontFamily: 'Manrope, sans-serif' }}
                  >
                    {openRight ? 'Hide' : 'See why'}
                  </span>
                  <ChevronDown
                    size={14}
                    style={{
                      color: '#fff',
                      transform: openRight ? 'rotate(180deg)' : 'rotate(0)',
                      transition: 'transform 180ms',
                      marginLeft: 'auto',
                    }}
                    strokeWidth={2.75}
                  />
                </div>
              </>
            ) : (
              <div className="flex items-center gap-2 py-1">
                <span className="text-base font-black text-white/60">→</span>
                <span className="text-sm font-bold text-white/70 uppercase tracking-wide">
                  No coverage yet
                </span>
              </div>
            )}
          </button>

          <AnimatePresence>
            {openRight && hasAnalyst && (
              <motion.div
                initial={{ height: 0, opacity: 0 }}
                animate={{ height: 'auto', opacity: 1 }}
                exit={{ height: 0, opacity: 0 }}
                transition={{ duration: 0.18 }}
                className="overflow-hidden"
                data-testid={`analyst-projection-detail-${entity.canonical_id}`}
              >
                <div
                  className="px-4 pb-3"
                  style={{ borderTop: '1px solid rgba(255,255,255,0.08)', paddingTop: 12 }}
                >
                  {analystDetail?.prediction && (
                    <p
                      className="text-[13px] leading-relaxed text-white font-semibold mb-3 italic"
                      style={{ fontFamily: 'Manrope, sans-serif' }}
                    >
                      "{analystDetail.prediction}"
                    </p>
                  )}
                  {/* Target band */}
                  {(analystDetail?.target_high || analystDetail?.target_low) && (
                    <div className="mb-3">
                      <div className="text-[11px] font-bold uppercase tracking-wider text-white/70 mb-1">
                        Target band
                      </div>
                      <div className="flex items-baseline gap-3 flex-wrap">
                        {analystDetail.target_low != null && (
                          <span className="text-[12px] font-bold text-red-300" style={{ fontFamily: 'JetBrains Mono, monospace' }}>
                            Low {fmtPrice(analystDetail.target_low)}
                          </span>
                        )}
                        <span className="text-base font-black" style={{ color: acDir.color, fontFamily: 'JetBrains Mono, monospace' }}>
                          {fmtPrice(analystDetail.target_price ?? ac.target_price)}
                        </span>
                        {analystDetail.target_high != null && (
                          <span className="text-[12px] font-bold text-emerald-300" style={{ fontFamily: 'JetBrains Mono, monospace' }}>
                            High {fmtPrice(analystDetail.target_high)}
                          </span>
                        )}
                      </div>
                    </div>
                  )}
                  {/* Recommendations */}
                  {analystDetail?.recommendations && (
                    <div className="mb-3">
                      <div className="text-[11px] font-bold uppercase tracking-wider text-white/70 mb-1.5">
                        Analyst breakdown
                      </div>
                      <RecBreakdown recs={analystDetail.recommendations} />
                    </div>
                  )}
                  {analystDetail?.why && (
                    <div className="mb-2">
                      <div className="text-[11px] font-bold uppercase tracking-wider text-white/70 mb-1">Why</div>
                      <p className="text-[12px] leading-relaxed text-white/90" style={{ fontFamily: 'Manrope, sans-serif' }}>
                        {analystDetail.why}
                      </p>
                    </div>
                  )}
                  {analystDetail?.what_it_means && (
                    <div className="mb-2">
                      <div className="text-[11px] font-bold uppercase tracking-wider text-white/70 mb-1">
                        What it means for you
                      </div>
                      <p className="text-[12px] leading-relaxed text-white font-semibold italic" style={{ fontFamily: 'Manrope, sans-serif' }}>
                        "{analystDetail.what_it_means}"
                      </p>
                    </div>
                  )}
                  {(analystDetail?.sources || ac.sources)?.length > 0 && (
                    <div className="flex items-center gap-1.5 flex-wrap mt-2 pt-2"
                         style={{ borderTop: '1px solid rgba(255,255,255,0.06)' }}>
                      <span className="text-[10px] font-bold uppercase text-white/60">Sources:</span>
                      {(analystDetail?.sources || ac.sources || []).map(s => (
                        <span key={s} className="text-[10px] font-bold px-1.5 py-0.5 rounded text-white/80"
                              style={{ background: 'rgba(255,255,255,0.06)' }}>
                          {s}
                        </span>
                      ))}
                    </div>
                  )}
                  {loadingAnalyst && !analystDetail && (
                    <p className="text-[11px] text-white/60 italic">Loading details…</p>
                  )}
                </div>
              </motion.div>
            )}
          </AnimatePresence>
        </div>
      </div>
    </div>
  );
}


/**
 * Renders the Current Trend score breakdown as horizontal bars.
 * Each row: label + score + bar.
 *
 * For reseller cards we render the projection-engine `buckets` (MARKET, DEMAND,
 * SOCIAL, EDITORIAL, VOLUME) instead of the investor `score_breakdown` so the
 * Trend Score expand panel actually explains "how we got 83".
 */
function BreakdownBars({ breakdown, dirColor, buckets, isReseller }) {
  if (isReseller && buckets && Object.keys(buckets).length > 0) {
    // Order matches the reseller_signals 5-bucket spec.
    const order = ['market', 'demand', 'social', 'editorial', 'volume'];
    const items = order
      .filter(k => buckets[k] != null)
      .map(k => ({ key: k, label: k.toUpperCase(), score: Number(buckets[k]?.score) || 0 }));
    if (items.length === 0) {
      return <p className="text-[11px] text-white/60 italic">No bucket data — fresh entity.</p>;
    }
    return (
      <div className="space-y-1.5">
        {items.map(it => {
          const pct = Math.round(it.score * 100);
          return (
            <div key={it.key} className="flex items-center gap-2 text-[12px]" style={{ fontFamily: 'Manrope, sans-serif' }}>
              <span className="w-[80px] text-white font-black tracking-wider">{it.label}</span>
              <div className="flex-1 h-2 rounded-full overflow-hidden" style={{ background: 'rgba(255,255,255,0.06)' }}>
                <div className="h-full rounded-full" style={{ width: `${pct}%`, background: dirColor, opacity: 0.85 }} />
              </div>
              <span className="w-9 text-right text-white font-black tabular-nums" style={{ fontFamily: 'JetBrains Mono, monospace' }}>
                {pct}%
              </span>
            </div>
          );
        })}
      </div>
    );
  }
  const items = [
    { key: 'editorial_velocity', label: 'News velocity',       max: 30 },
    { key: 'source_diversity',   label: 'Source diversity',    max: 15 },
    { key: 'signal_type_diversity', label: 'Signal diversity', max: 20 },
    { key: 'cross_type_bonus',   label: 'Cross-source signals',max: 10 },
  ];
  const visible = items.filter(it => breakdown[it.key] != null);
  if (visible.length === 0) {
    return (
      <p className="text-[11px] text-white/60 italic">No breakdown available — fresh entity.</p>
    );
  }
  return (
    <div className="space-y-1.5">
      {visible.map(it => {
        const v = Number(breakdown[it.key]) || 0;
        const pct = Math.min(100, (v / it.max) * 100);
        return (
          <div key={it.key} className="flex items-center gap-2 text-[12px]" style={{ fontFamily: 'Manrope, sans-serif' }}>
            <span className="w-[140px] text-white/90 font-semibold">{it.label}</span>
            <div className="flex-1 h-2 rounded-full overflow-hidden" style={{ background: 'rgba(255,255,255,0.06)' }}>
              <div className="h-full rounded-full" style={{ width: `${pct}%`, background: dirColor, opacity: 0.85 }} />
            </div>
            <span className="w-7 text-right text-white font-bold tabular-nums" style={{ fontFamily: 'JetBrains Mono, monospace' }}>
              {Math.round(v)}
            </span>
          </div>
        );
      })}
    </div>
  );
}


function RecBreakdown({ recs }) {
  const items = [
    { key: 'strong_buy',  label: 'Strong Buy',  color: '#10B981' },
    { key: 'buy',         label: 'Buy',         color: '#34D399' },
    { key: 'hold',        label: 'Hold',        color: '#D4D4D8' },
    { key: 'sell',        label: 'Sell',        color: '#F87171' },
    { key: 'strong_sell', label: 'Strong Sell', color: '#EF4444' },
  ];
  const total = items.reduce((s, it) => s + (recs[it.key] || 0), 0) || 1;
  return (
    <div className="space-y-1">
      {items.map(it => {
        const v = recs[it.key] || 0;
        if (v === 0) return null;
        const pct = (v / total) * 100;
        return (
          <div key={it.key} className="flex items-center gap-2 text-[12px]" style={{ fontFamily: 'Manrope, sans-serif' }}>
            <span className="w-[90px] font-bold" style={{ color: it.color }}>{it.label}</span>
            <div className="flex-1 h-2 rounded-full overflow-hidden" style={{ background: 'rgba(255,255,255,0.06)' }}>
              <div className="h-full rounded-full" style={{ width: `${pct}%`, background: it.color }} />
            </div>
            <span className="w-7 text-right text-white font-bold tabular-nums" style={{ fontFamily: 'JetBrains Mono, monospace' }}>
              {v}
            </span>
          </div>
        );
      })}
    </div>
  );
}
